import React from 'react';
import { type GeneralSettings } from '../../api/types.js';
import ToggleRow from './ToggleRow.js';
import { InputField, SelectField } from './Field.js';
import SaveBar from './SaveBar.js';
import { useEditable } from './useEditable.js';
import { useTranslation } from '../../i18n/useTranslation.js';

interface Props {
  value: GeneralSettings;
  onSave: (next: GeneralSettings) => Promise<void>;
}

function GeneralTab({ value, onSave }: Props) {
  const { t } = useTranslation();
  const { draft, set, dirty, saving, reset, save } = useEditable(value, onSave);

  const LANGUAGES = [
    { value: 'en', label: t('general.langEn') },
    { value: 'hu', label: t('general.langHu') },
    { value: 'he', label: t('general.langHe') },
    { value: 'de', label: t('general.langDe') },
  ];

  const TIMEZONES = [
    { value: 'Europe/Budapest', label: 'Europe/Budapest (CET+1)' },
    { value: 'Europe/London', label: 'Europe/London (GMT)' },
    { value: 'Asia/Jerusalem', label: 'Asia/Jerusalem (IST+2)' },
    { value: 'UTC', label: 'UTC' },
  ];

  const DATE_FORMATS = [
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)' },
  ];

  return (
    <div className="sp on">
      <div className="sp-title">{t('general.title')}</div>
      <div className="sp-subtitle">{t('general.subtitle')}</div>

      <div className="sg">
        <div className="sg-title">{t('general.interface')}</div>
        <div className="sg-grid">
          <SelectField
            label={t('general.interfaceLanguage')}
            value={draft.language}
            options={LANGUAGES}
            onChange={(v) => set('language', v)}
          />
          <SelectField
            label={t('general.timezone')}
            value={draft.timezone}
            options={TIMEZONES}
            onChange={(v) => set('timezone', v)}
          />
          <SelectField
            label={t('general.dateFormat')}
            value={draft.dateFormat}
            options={DATE_FORMATS}
            onChange={(v) => set('dateFormat', v)}
          />
          <InputField
            label={t('general.mapDefaultZoom')}
            type="number"
            min={10}
            max={19}
            value={draft.mapDefaultZoom}
            onChange={(v) => set('mapDefaultZoom', Number(v))}
          />
        </div>
      </div>

      <div className="sg">
        <div className="sg-title">{t('general.display')}</div>
        <ToggleRow
          label={t('general.darkMode')}
          sub={t('general.darkModeSub')}
          checked={draft.darkMode}
          disabled
          onChange={() => {}}
        />
        <ToggleRow
          label={t('general.showTrails')}
          sub={t('general.showTrailsSub')}
          checked={draft.showTrails}
          onChange={(v) => set('showTrails', v)}
        />
        {/* <ToggleRow
          label="Auto-Pan on Critical Alert"
          sub="Map flies to the suspect on a 50m breach"
          checked={draft.autoPanOnAlert}
          onChange={(v) => set('autoPanOnAlert', v)}
        /> */}
        <ToggleRow
          label={t('general.showPerimeterLabels')}
          sub={t('general.showPerimeterLabelsSub')}
          checked={draft.showPerimeterLabels}
          onChange={(v) => set('showPerimeterLabels', v)}
        />
        <ToggleRow
          label={t('general.toastNotifications')}
          sub={t('general.toastNotificationsSub')}
          checked={draft.toastNotifications}
          onChange={(v) => set('toastNotifications', v)}
        />
      </div>

      {/* <div className="sg">
        <div className="sg-title">Simulation (Demo Mode)</div>
        <div className="sg-grid">
          <SelectField
            label="Default Simulation Speed"
            value={draft.defaultSimSpeed}
            options={SIM_SPEEDS}
            onChange={(v) => set('defaultSimSpeed', Number(v))}
          />
          <InputField
            label="Tick Interval (ms)"
            type="number"
            min={200}
            max={5000}
            value={draft.tickIntervalMs}
            onChange={(v) => set('tickIntervalMs', Number(v))}
          />
        </div>
        <ToggleRow
          label="New Suspect Injection"
          sub="Inject D-001 at the scripted moment in the demo"
          checked={draft.newSuspectInjection}
          onChange={(v) => set('newSuspectInjection', v)}
        />
        <ToggleRow
          label="Scripted Demo Narrative"
          sub="Show pre-baked alerts and intel ticker items"
          checked={draft.scriptedNarrative}
          onChange={(v) => set('scriptedNarrative', v)}
        />
      </div> */}

      <SaveBar dirty={dirty} saving={saving} onCancel={reset} onSave={save} />
    </div>
  );
}

export default GeneralTab;
